import React, { useState } from "react";
import ContentView from "modules/common/components/authoring/contentView/contentView";
import { Comment, PostView as IPostView } from '../../models';
import { ContentType } from "modules/common/models";
import PostTypeBanner from "modules/common/components/banners/postTypeBanner";
import Compliance from "./compliance/compliance";
import { getTranslatedComplianceText } from "utils/getTranslatedContent";
import Reactions from "./reactions/reactions";
import Comments from "./comments/comments";
import BreakingFeaturedBanner from "../banners/breakingFeaturedBanner";
import moment from "moment";
import Topics from "../post-feed/components/topics/topics";
import { connect, ConnectedProps } from "react-redux";
import { GlobalApplicationState } from "globalApplicationState";

interface IPostViewContentProps {
    post: IPostView;
    onUpdateComments: (comments: Comment[]) => void;
    onUpdateCommentSubscriptions: (subscribed: boolean) => void
    defaultLCID: string;
    shareButtonComponent?: React.ReactNode;
    shareScreenComponent?: React.ReactNode;
}

const PostViewContent: React.FC<PropsWithRedux> = ({
    post,
    reactionsEnabled,
    reactions,
    commentsEnabled,
    onUpdateComments,
    onUpdateCommentSubscriptions,
    defaultLCID,
    shareButtonComponent,
    shareScreenComponent
}) => {
    const [activeLcid, setActiveLcid] = useState<string>(defaultLCID);

    return <ContentView
        content={{...post}}
        contentType={ContentType.Post}
        typeBannerComponent={<PostTypeBanner {...post} full lcid={activeLcid} isRestricted={post.isRestricted}/>}
        onChangeActiveLcid={(activeLcid) => setActiveLcid(activeLcid)}
        pageContentComponent={
            <Compliance
                postId={post.id}
                complianceText={getTranslatedComplianceText(post.translatedContent, activeLcid)}
                postType={post.postType}
                stats={post.stats}
            />
        }
        bottomPageContentComponent={
            <>
                <Reactions
                    reactionsEnabledOnTenant={reactionsEnabled}
                    reactions={reactions}
                    postType={post.postType}
                    reactingEnabledOnPost={post.reactingEnabled}
                    stats={post.stats}
                />
                <div className="comments">
                    <Comments
                        id={post.id}
                        commentsEnabledOnTenant={commentsEnabled}
                        commentsEnabledOnPost={post.commentingEnabled}
                        comments={post.comments}
                        isSubscribed={post.isPostNotificationsActive}
                        showSubscription={post.userCommentNotificationSettings !== "none"}
                        onUpdateComments={onUpdateComments}
                        onUpdateCommentSubscriptions={onUpdateCommentSubscriptions}
                    />
                </div>
            </>
        }
        featureBannerComponent={
            <BreakingFeaturedBanner detailed activeLcid={activeLcid} isBreaking={post.isBreaking} isFeatured={post.isFeatured} />
        }
        pageContentInfoComponent={
            <>
                <div className="post-content-view-published-time">
                    {
                        `Published ${moment(post.datePublished!).format("MMMM D, YYYY")}`
                    }
                </div>
                {
                    !!post.tags && !!post.tags.length &&
                    <Topics 
                        topics={post.tags} 
                        topicLabel={"Topics: "} 
                        className="post-content-view-tags" 
                        iconSize="small"
                    />
                }
            </>
        }
        defaultLCID={activeLcid}
        shareButtonComponent={shareButtonComponent}
        shareScreenComponent={shareScreenComponent}
    />;
};

const connector = connect(
    (state: GlobalApplicationState, ownProps: IPostViewContentProps) => ({
        ...ownProps,
        reactionsEnabled: state.settings.clientSettings.reactionSystem && state.settings.clientSettings.reactionSystem.enabled,
        commentsEnabled: state.settings.clientSettings.commentSystem && state.settings.clientSettings.commentSystem.enabled,
        reactions: state.settings.clientSettings.reactions
    })
);
type PropsWithRedux = ConnectedProps<typeof connector>;

export default connector(PostViewContent);
